import {createTheme, TabBar, TabItem, View, ViewContent, ViewHeader} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {BoxListView} from "./BoxListView";
import {NfcReaderView} from "./NfcReaderView";
import {PaymentTerminalView} from "./PaymentTerminalView";
import {SystemView} from "./SystemView";
import {LockListView} from "./LockListView";
import {BarcodeReaderView} from "./BarcodeReaderView";
import {AccessLevel, Controller} from "../controller/Controller";
import {themeOptions} from "../theme";
import {useLocalization} from "../../i18n";
import {stylesheet} from "typestyle";

enum Tab {
    Boxes,
    Locks,
    System,
    NfcReader,
    PaymentTerminal,
    BarcodeReader,
}

interface SettingsViewProps {
    controller: Controller;
    secondary: boolean;
    accessLevel: AccessLevel;
}

export function SettingsView({controller, secondary, accessLevel}: SettingsViewProps) {
    const {t} = useLocalization();
    const [activeTab, setActiveTab] = useState<Tab>(Tab.Boxes);

    const content = useMemo(() => {
        switch (activeTab) {
            case Tab.Boxes:
                return <BoxListView controller={controller} accessLevel={accessLevel} secondary={secondary}/>;
            case Tab.Locks:
                return <LockListView controller={controller}/>;
            case Tab.System:
                return <SystemView controller={controller}/>;
            case Tab.NfcReader:
                return <NfcReaderView controller={controller}/>;
            case Tab.PaymentTerminal:
                return <PaymentTerminalView controller={controller}/>;
            case Tab.BarcodeReader:
                return <BarcodeReaderView controller={controller}/>;
            default:
                return <div>Tab not implemented</div>;
        }
    }, [activeTab, controller, secondary, accessLevel]);

    const handleBoxItemClick = useCallback(() => setActiveTab(Tab.Boxes), []);
    const handleLockItemClick = useCallback(() => setActiveTab(Tab.Locks), []);
    const handleSystemItemClick = useCallback(() => setActiveTab(Tab.System), []);
    const handleNfcReaderItemClick = useCallback(() => setActiveTab(Tab.NfcReader), []);
    const handlePaymentTerminalItemClick = useCallback(() => setActiveTab(Tab.PaymentTerminal), []);
    const handleBarcodeReaderItemClick = useCallback(() => setActiveTab(Tab.BarcodeReader), []);

    return (
        <View className={settingsTheme}>
            <ViewHeader>
                <TabBar className={styles.topBar}>
                    <TabItem active={activeTab == Tab.Boxes} onClick={handleBoxItemClick}>
                        {t("Boxes")}
                    </TabItem>
                    {(accessLevel == AccessLevel.FullAccess) && (
                        <TabItem active={activeTab == Tab.Locks} onClick={handleLockItemClick}>
                            {t("Locks")}
                        </TabItem>
                    )}
                    {(accessLevel == AccessLevel.FullAccess) && (
                        <TabItem active={activeTab == Tab.System} onClick={handleSystemItemClick}>
                            {t("System")}
                        </TabItem>
                    )}
                    <TabItem active={activeTab == Tab.NfcReader} onClick={handleNfcReaderItemClick}>
                        {t("NfcReader")}
                    </TabItem>
                    <TabItem active={activeTab == Tab.PaymentTerminal} onClick={handlePaymentTerminalItemClick}>
                        {t("PaymentTerminal")}
                    </TabItem>
                    <TabItem active={activeTab == Tab.BarcodeReader} onClick={handleBarcodeReaderItemClick}>
                        {t("BarcodeReader")}
                    </TabItem>
                </TabBar>
            </ViewHeader>
            <ViewContent>
                {content}
            </ViewContent>
        </View>
    );
}

const settingsTheme = createTheme({
    ...themeOptions,
    colorBackground: "#fff",
    colorForeground: "#333",
});

const styles = stylesheet({
    topBar: {
        width: '100%'
    }
})